import { useCallback } from 'react';
import { useT } from '@dia/i18n';
import { loadPower, loadWater, useOutbox, waterPayloads, type Tank } from '@dia/db';
import { supabase } from '../supabase';
import { RequireAuth, useIdentity } from './lib/identity';
import { useLoad } from './lib/data';
import { useNow } from './lib/power';
import { Page } from './lib/ui';
import { EnergiaView } from './EnergiaView';

/**
 * Energia: battery, sun, house use and the generator (CLAUDE.md Section 7,
 * Phase 3). The document is worked out on the island's side of the wire every
 * time Home Assistant reports, so this screen only reads it: network first,
 * the phone's own copy second, re-read every minute while open.
 *
 * Water is the one thing typed in by hand (the tank sensors went back). A level
 * is queued through the outbox like every other write, so it is kept with no
 * signal and sent later, and it shows on this screen as "waiting to send" in
 * the meantime.
 *
 * Any role on the staff may read it (migration 026) and enter water (migration
 * 027). The database says no to everybody else, so a route here is a way in and
 * not a permission.
 */
export function Energia() {
  return <RequireAuth>{() => <EnergiaBody />}</RequireAuth>;
}

function EnergiaBody() {
  const { t } = useT();
  const { identity } = useIdentity();
  const { enqueue } = useOutbox();
  const power = useLoad(() => loadPower(supabase), 'power', 60_000);
  const water = useLoad(() => loadWater(supabase), 'water', 60_000);
  const now = useNow();
  const reloadWater = water.reload;

  const saveWater = useCallback(
    async (levels: Partial<Record<Tank, number>>) => {
      const readAt = new Date().toISOString();
      for (const row of waterPayloads(levels, readAt, identity?.staffId ?? null)) {
        await enqueue({ table: 'water_levels', payload: row });
      }
      await reloadWater();
    },
    [enqueue, identity?.staffId, reloadWater],
  );

  return (
    <Page title={t('ops.power.title')} back="/mas">
      <EnergiaView
        view={power.data}
        now={now}
        loading={power.loading}
        onRetry={power.reload}
        water={water.data}
        onSaveWater={saveWater}
      />
    </Page>
  );
}
